import { useQuery } from '@tanstack/react-query';
import { type ColumnDef } from '@tanstack/react-table';
import { Card, Input, Page, PageStatus } from 'corso-admin-ui';
import { type CommandAdminApi } from 'corso-types';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { match, P } from 'ts-pattern';
import { useDebounceValue } from 'usehooks-ts';
import api from '~/api';
import {
  DataTable,
  DataTableStatus,
  sharedTableDefinitions,
} from '~/components/dataTable';

type User = CommandAdminApi.ResponseBody<'/user', 'get'>[number];

const { idColumn, createdOnColumn } = sharedTableDefinitions;

const userColumns: ColumnDef<User>[] = [
  idColumn<User>({ meta: { align: 'left' } }),
  { accessorKey: 'email', header: 'Email', enableSorting: true },
  { accessorKey: 'firstName', header: 'First Name' },
  { accessorKey: 'lastName', header: 'Last Name' },

  createdOnColumn<User>(),
];

export default function AllUsersPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm] = useDebounceValue(searchTerm, 250);
  const navigate = useNavigate();

  const query = useQuery({
    queryKey: ['users', { searchTerm: debouncedSearchTerm }],
    queryFn: () => api.users.search(debouncedSearchTerm),
  });

  return (
    <Page title="All Users" size="fullWidth">
      <Card>
        <Input
          id="user-search"
          label="Search users by name or email"
          labelVisuallyHidden
          type="search"
          placeholder="Search users by name or email"
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
        />

        {match(query)
          .with({ status: 'pending' }, () => (
            <PageStatus.Loading title="Loading users" />
          ))
          .with({ status: 'error' }, () => (
            <PageStatus.Error
              title="Failed to load users"
              error={query.error}
            />
          ))
          .with({ status: 'success', data: P.select() }, (data) =>
            data.length > 0 ? (
              <DataTable
                data={data}
                columns={userColumns}
                hidePagination
                onRowClick={(user) => navigate(`${user.id}`)}
              />
            ) : (
              <DataTableStatus
                title="No users found"
                icon="accounts"
                details="Please adjust your search and try again"
              />
            ),
          )
          .exhaustive()}
      </Card>
    </Page>
  );
}
